import { Injectable }   from '@angular/core';

import { Field, Condition, NumberField, IntegerField, UrlField, MultiSelectListField } from '../models';
import { TranslatorService } from './translator.service';

@Injectable()
export class ConditionService {

    readonly OP_HAS:string = "HAS";
    readonly OP_NHAS:string = "NHAS";
    readonly OP_NEQ:string = "NEQ";
    readonly OP_EQ:string = "EQ";
    readonly OP_GTE:string = "GTE";
    readonly OP_GT:string = "GT";
    readonly OP_LTE:string = "LTE";
    readonly OP_LT:string = "LT";
    
    constructor(
        public translatorService: TranslatorService
    ) { }
    
    getCondition(conditions, name) {
        if (conditions) {
            for(let i = 0; i < conditions.length; i++) {
                let c = conditions[i];
                if (c.name == name) {
                    return c;
                }
            }
        }
        return null;
    }

    public getFormField(formFields:Array<Field<any>>, key:string):Field<any> {
        for (let i = 0; i < formFields.length; i++) {
            let f = formFields[i];
            if (f.key == key) {
                return f;
            }
        };
        return null;
    }

    public getForumlaResult(formulaResults:any, name:string) {
        for (let i = 0; i < formulaResults.length; i++) {
            let f = formulaResults[i];
            if (f.name == name) {
                return f;
            }
        };
        return null;
    }

    // item values are single table view item, most likely non-form field item
    // each item contain flattened field values with no field properties
    evaluateItemValues(conditions, formulaResults, item:any, local:any = null) {
        conditions.forEach(c => {
            if (c.field_key) {
                this.evaluateConditionByItem(c, null, item);
            } else if (c.formula_name) {
                let forumla = this.getForumlaResult(formulaResults, c.formula_name);
                if (forumla) {
                    this.evaluateConditionByValue(c, null, forumla.result);
                }
            }
        });
        // eval conditions with dependencies
        return this.evaluateConditionWithDependencies(conditions, local);
    }

    // form values are single item that is represented by an array of Fields 
    // each Field contains certain properties, ie display_type, field_key
    evaluateFormValues(conditions, oldFormFields:Array<Field<any>>, newFormFields:Array<Field<any>>, formulaResults:any = null) {
        // eval simple conditions
        conditions.forEach(c => {
            // Step 1.  backup the original condition value
            if (c.org_condition_value == undefined) c.org_condition_value = c.value;
            // Step 2.  if needed, translate condition value in case it contains identifier(s)
            if (c.org_condition_value && Number.isNaN(c.org_condition_value)) {
                var translated_condition_value =  this.translatorService.translate(oldFormFields, newFormFields, c.org_condition_value);
                c.value = (translated_condition_value && translated_condition_value != c.org_condition_value) ? translated_condition_value : c.org_condition_value;
            }
            if (c.field_key) {
                let newField = this.getFormField(newFormFields, c.field_key);
                let oldField = this.getFormField(oldFormFields, c.field_key);
                if (newField) {
                    c.field = newField;   // link field to condition
                    this.evaluateCondition(c, formulaResults, oldField, newField);
                }
            } else if (c.formula_name) {
                let forumla = this.getForumlaResult(formulaResults, c.formula_name);
                if (forumla) {
                    this.evaluateConditionByValue(c, null, forumla.result);
                }
            }
        });
        // eval conditions with dependencies
        // return this.evaluateConditionWithDependencies(conditions);
        // eval conditions with dependencies
        conditions.forEach(c => {
            c.invalid_conditions = [];
            if (c.dependencies) {
                let valid_count: number = 0;
                let changed_count: number = 0;
                // valid check
                let invalid_conditions:any[] = [];
                c.dependencies.conditions.forEach(name => {
                    let sub_condition = this.getCondition(conditions, name);
                    if (sub_condition) {
                        if (sub_condition.result) {
                            valid_count++;
                        } else if (sub_condition.result == false) {
                            if (sub_condition.dependencies) {
                                sub_condition.dependencies.conditions.forEach(c => {
                                    let dc = this.getCondition(conditions, c);
                                    if (dc && dc.invalid_conditions && dc.invalid_conditions.length > 0) {
                                        dc.invalid_conditions.forEach(ic => {
                                            invalid_conditions.push(ic);
                                        })
                                    } else {
                                        invalid_conditions.push(dc);
                                    }
                                })
                            } else {
                                invalid_conditions.push(sub_condition);
                            }
                        }
                    }
                });
                // changed check
                let unchanged_conditions:any[] = [];
                c.dependencies.conditions.forEach(name => {
                    let sub_condition = this.getCondition(conditions, name);
                    if (sub_condition) {
                        if (sub_condition.changed) {
                            changed_count++;
                        } else {
                            if (sub_condition.dependencies) {
                                sub_condition.dependencies.conditions.forEach(c => {
                                    let dc = this.getCondition(conditions, c);
                                    unchanged_conditions.push(dc.unchanged_conditions);
                                })
                            } else {
                                unchanged_conditions.push(sub_condition);
                            }
                        }
                    }
                });        
                c.result = true;
                c.error = null;
                switch (c.dependencies.op) {
                    case "one_valid":
                        if (valid_count == 0) {
                            c.result = false;
                            c.error = { 'incorrect': false };
                            c.invalid_conditions = invalid_conditions;
                        }
                        break;
                    case "one_changed":
                        if (changed_count == 0) {
                            c.result = false;
                            c.error = { 'incorrect': false };
                            c.unchanged_conditions = unchanged_conditions;
                        }
                        break;
                    case "all_valid":
                        if (valid_count != c.dependencies.conditions.length) {
                            c.result = false;
                            c.error = { 'incorrect': false };
                            c.invalid_conditions = invalid_conditions;
                        }
                        break;
                }
            }
        })
        return conditions;
    }

    evaluateConditionWithDependencies(conditions, local:any = null) {
        // eval conditions with dependencies
        conditions.forEach(c => {
            c.invalid_conditions = [];
            if (c.dependencies) {
                let valid_count: number = 0;
                let changed_count: number = 0;
                // valid check
                let invalid_conditions:any[] = [];
                c.dependencies.conditions.forEach(name => {
                    if (name.indexOf('@{') >= 0) {
                        var local_condition = name.replace('@{', '').replace('}', '');
                        if (local && local[local_condition]) {
                            valid_count++;
                        }
                    } else {
                        let sub_condition = this.getCondition(conditions, name);
                        if (sub_condition) {
                            if (sub_condition.result) {
                                valid_count++;
                            } else if (sub_condition.result == false) {
                                if (sub_condition.dependencies) {
                                    sub_condition.dependencies.conditions.forEach(c => {
                                        let dc = this.getCondition(conditions, c);
                                        if (dc && dc.invalid_conditions && dc.invalid_conditions.length > 0) {
                                            dc.invalid_conditions.forEach(ic => {
                                                invalid_conditions.push(ic);
                                            })
                                        } else {
                                            invalid_conditions.push(dc);
                                        }
                                    })
                                } else {
                                    invalid_conditions.push(sub_condition);
                                }
                            }
                        }
                    }
                });
                // changed check
                let unchanged_conditions:any[] = [];
                c.dependencies.conditions.forEach(name => {
                    let sub_condition = this.getCondition(conditions, name);
                    if (sub_condition) {
                        if (sub_condition.changed) {
                            changed_count++;
                        } else {
                            if (sub_condition.dependencies) {
                                sub_condition.dependencies.conditions.forEach(c => {
                                    let dc = this.getCondition(conditions, c);
                                    if (dc) unchanged_conditions.push(dc.unchanged_conditions);
                                })
                            } else {
                                unchanged_conditions.push(sub_condition);
                            }
                        }
                    }
                });        
                c.result = true;
                c.error = null;
                switch (c.dependencies.op) {
                    case "one_valid":
                        if (valid_count == 0) {
                            c.result = false;
                            c.error = { 'incorrect': false };
                            c.invalid_conditions = invalid_conditions;
                        }
                        break;
                    case "one_changed":
                        if (changed_count == 0) {
                            c.result = false;
                            c.error = { 'incorrect': false };
                            c.unchanged_conditions = unchanged_conditions;
                        }
                        break;
                    case "all_valid":
                        if (valid_count != c.dependencies.conditions.length) {
                            c.result = false;
                            c.error = { 'incorrect': false };
                            c.invalid_conditions = invalid_conditions;
                        }
                        break;
                }
            }
        })
        return conditions;        
    }

    //#region Form Field evaluation
    evaluateLengthCondition(condition:Condition, oldField:Field<any>, newField:Field<any>) {
        let error:any = null;
        if (condition.length > 0 && !newField.value) {
            error = { 'incorrect': true };
        } else if (condition.length == 0 && (newField.value && newField.value.length > 0)) {
            error = { 'incorrect': true };
        } else {
            var string_value = newField.value.toString();
            // need to handle data_type
            switch (condition.op) {
                case this.OP_NEQ:
                    error = (string_value.length == condition.length) ? { 'incorrect': true } : null;
                    break; 
                case this.OP_EQ:
                    error = (string_value.length != condition.length) ? { 'incorrect': true } : null;
                    break;
                case this.OP_GTE:
                    error = (string_value.length < condition.length) ? { 'incorrect': true } : null;
                    break;
                case this.OP_GT:
                    error = (string_value.length <= condition.length) ? { 'incorrect': true } : null;
                    break;
                case this.OP_LTE:
                    error = (string_value.length > condition.length) ? { 'incorrect': true } : null;
                    break;
                case this.OP_LT:
                    error = (string_value.length >= condition.length) ? { 'incorrect': true } : null;
                    break;
            }
        }
        return error;
    }

    evaluateDataTypeCondition(condition:Condition, oldField:Field<any>, newField:Field<any>) {
        let error:any = null;
        var string_value = "";
        var numeric_value = 0;
        if (newField.value && newField.value.toString().length > 0) {
            numeric_value = newField.value;
            string_value = newField.value.toString();
        }

        var bIsInteger: boolean = Number.isInteger(numeric_value);
        var bIsNaN: boolean = Number.isNaN(numeric_value);

        if (condition.op == this.OP_EQ) {
            switch (condition.data_type) {
                case IntegerField.control_type: if (!bIsInteger)    error = { 'incorrect': true }; break;
                case NumberField.control_type:  if (bIsNaN)         error = { 'incorrect': true }; break;
            }
        } else if (condition.op == this.OP_NEQ) {
            switch (condition.data_type) {
                case IntegerField.control_type: if (bIsInteger)     error = { 'incorrect': true }; break;
                case NumberField.control_type:  if (!bIsNaN)        error = { 'incorrect': true }; break;
            }
        }

        return error;
    }

    evaluateValueCondition(condition:Condition, oldField:Field<any>, newField:Field<any>) {
        let error:any = null;
        // need to handle data_type
        let numeric_value:number = null;
        var condition_numeric_value:number = null;
        switch (newField.display_type)
        {
            case NumberField.control_type: 
                numeric_value = newField.value ? parseFloat(newField.value) : 0; 
                condition_numeric_value = condition.value ? parseFloat(condition.value) : 0; 
                break;
        }        

        switch (condition.op) {
            case this.OP_NHAS:
                if (condition.value && condition.value.length > 0) {
                    if (newField.value == "" || newField.value == undefined || newField.value == null) {
                    } else if (newField.value && newField.value.indexOf(condition.value) < 0) {
                    } else {
                        error = { 'incorrect': true }
                    }
                }
                break;
            case this.OP_HAS:
                if (condition.value && condition.value.length > 0) {
                    if (newField.value == "" || newField.value == undefined || newField.value == null) {
                        error = { 'required': true }
                    } else if (newField.value && newField.value.indexOf(condition.value) < 0) {
                        error = { 'incorrect': true }
                    }
                }
                break; 
            case this.OP_NEQ:
                if (!condition.value && !newField.value) {
                    error = { 'required': true }
                } else if (numeric_value != null && condition_numeric_value != null) {
                    error = (numeric_value != condition_numeric_value) ? null :  { 'incorrect': true };
                } else if (condition.value && newField.value == condition.value) {
                    error = { 'incorrect': true }
                }
                break; 
            case this.OP_EQ:
                if (!condition.value && newField.value) {  // check empty or null
                    error = { 'incorrect': true }
                } else if (numeric_value != null && condition_numeric_value != null) {
                    error = (numeric_value == condition_numeric_value) ? null :  { 'incorrect': true };
                } else if (condition.value && newField.value != condition.value) { // check value
                    error = { 'incorrect': true }                    
                }
                break;
            case this.OP_GTE:
                if (numeric_value != null && condition_numeric_value != null) {
                    error = (numeric_value >= condition_numeric_value) ? null :  { 'incorrect': true };
                } else {
                    error = (newField.value && condition.value && newField.value >= condition.value) ? null :  { 'incorrect': true };
                }
                break;
            case this.OP_GT:
                if (numeric_value != null && condition_numeric_value != null) {
                    error = (numeric_value > condition_numeric_value) ? null :  { 'incorrect': true };
                } else {
                    error = (newField.value && condition.value && newField.value > condition.value) ? null :  { 'incorrect': true };
                }
                break;
            case this.OP_LTE:
                if (numeric_value != null && condition_numeric_value != null) {
                    error = (numeric_value <= condition_numeric_value) ? null :  { 'incorrect': true };
                } else {
                    error = (newField.value && condition.value && newField.value <= condition.value) ? null :  { 'incorrect': true };
                }
                break;
            case this.OP_LT:
                if (numeric_value != null && condition_numeric_value != null) {
                    error = (numeric_value < condition_numeric_value) ? null :  { 'incorrect': true };
                } else {
                    error = (newField.value && condition.value && newField.value < condition.value) ? null :  { 'incorrect': true };
                }
                break;
        }
        
        return error;
    }

    evaluateCondition(condition:Condition, formulaResults, oldField:Field<any>, newField:Field<any>) {
        var old_value = oldField ? oldField.value : null;
        condition.eval_data_type = newField.display_type;
        this.evaluateConditionByValue(condition, old_value, newField.value);
    }
    //#endregion

    //#region value evaluation
    private evaluateConditionByItem(condition:Condition, formulaResults, item:any) {
        this.evaluateConditionByValue(condition, null, item[condition.field_key]);
    }

    private evaluateConditionByValue(condition:Condition, old_value:any, new_value:any) {
        condition.result = true;
        let error:any = null;
        if (condition.length && condition.length >= 0) {
            error = this.evaluateLengthConditionByValue(condition, old_value, new_value);
        } else if (condition.data_type && condition.data_type.length > 0) {
            error = this.evaluateDataTypeConditionByValue(condition, old_value, new_value);
        } else {
            error = this.evaluateValueConditionByValue(condition, condition.eval_data_type, old_value, new_value);
        }

        if (error) {
            condition.result = false;
            condition.error = error;
        }
    }

    private evaluateLengthConditionByValue(condition:Condition, old_value:string, new_value:any) {
        let error:any = null;
        var string_length = new_value ? new_value.toString().length : 0;
        // if (condition.length > 0 && !new_value) {
        //     error = { 'incorrect': true };
        // } else if (condition.length == 0 && (new_value && new_value.length > 0)) {
        //     error = { 'incorrect': true };
        // } else {
            //var string_value = new_value.toString();
            // need to handle data_type
            switch (condition.op) {
                case this.OP_NEQ:
                    error = (string_length == condition.length) ? { 'incorrect': true } : null;
                    break; 
                case this.OP_EQ:
                    error = (string_length != condition.length) ? { 'incorrect': true } : null;
                    break;
                case this.OP_GTE:
                    error = (string_length < condition.length) ? { 'incorrect': true } : null;
                    break;
                case this.OP_GT:
                    error = (string_length <= condition.length) ? { 'incorrect': true } : null;
                    break;
                case this.OP_LTE:
                    error = (string_length > condition.length) ? { 'incorrect': true } : null;
                    break;
                case this.OP_LT:
                    error = (string_length >= condition.length) ? { 'incorrect': true } : null;
                    break;
            }
        //}
        return error;
    }

    private evaluateDataTypeConditionByValue(condition:Condition, old_value:string, new_value:any) {
        let error:any = null;
        var string_value = "";
        var numeric_value = 0;
        if (new_value && new_value.toString().length > 0) {
            numeric_value = parseFloat(new_value.toString());
            string_value = new_value.toString();
        }

        var bIsInteger: boolean = Number.isInteger(numeric_value);
        var bIsNaN: boolean = Number.isNaN(numeric_value);
        var bIsUrl: boolean = this.validateUrl(string_value);
        var bIsEmail: boolean = this.validateEmail(string_value);

        if (condition.op == this.OP_EQ) {
            switch (condition.data_type) {
                case IntegerField.control_type: if (!bIsInteger)    error = { 'incorrect': true }; break;
                case NumberField.control_type:  if (!bIsNaN)        error = { 'incorrect': true }; break;
                case UrlField.control_type:     if (!bIsUrl)        error = { 'incorrect': true }; break;
            }
        } else if (condition.op == this.OP_NEQ) {
            switch (condition.data_type) {
                case IntegerField.control_type: if (bIsInteger)     error = { 'incorrect': true }; break;
                case NumberField.control_type:  if (bIsNaN)         error = { 'incorrect': true }; break;
                case UrlField.control_type:     if (bIsUrl)         error = { 'incorrect': true }; break;
            }
        }

        return error;
    }

    validateEmail(email) {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    }

    validateUrl(str) {
        var pattern = new RegExp('^(https?:\\/\\/)?'+ // protocol
          '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ // domain name
          '((\\d{1,3}\\.){3}\\d{1,3}))'+ // OR ip (v4) address
          '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // port and path
          '(\\?[;&a-z\\d%_.~+=-]*)?'+ // query string
          '(\\#[-a-z\\d_]*)?$','i'); // fragment locator
        return !!pattern.test(str);
      }

    private evaluateValueConditionByValue(condition:Condition, field_data_type:string, old_value:string, new_value:any) {
        let error:any = null;
        // need to handle data_type
        let numeric_value:number = null;
        var condition_numeric_value:number = null;
        switch (field_data_type)
        {
            case NumberField.control_type: 
                numeric_value = new_value ? parseFloat(new_value) : 0; 
                condition_numeric_value = condition.value ? parseFloat(condition.value) : 0; 
                break;
            // for now let's convert new_value array to a concatenated string.  Later, add array comparison support if there is a need to compare in array type
            case MultiSelectListField.control_type: 
                new_value = new_value.join();
                break;
        }        

        switch (condition.op) {
            case this.OP_NHAS:
                if (condition.value && condition.value.length > 0) {
                    if (new_value == "" || new_value == undefined || new_value == null) {
                    } else if (new_value && new_value.indexOf(condition.value) < 0) {
                    } else {
                        error = { 'incorrect': true }
                    }
                }
                break; 
            case this.OP_HAS:
                if (condition.value && condition.value.length > 0) {
                    if (new_value == "" || new_value == undefined || new_value == null) {
                        error = { 'required': true }
                    } else if (new_value && new_value.indexOf(condition.value) < 0) {
                        error = { 'incorrect': true }
                    }
                }
                break; 
            case this.OP_NEQ:
                if (!condition.value && !new_value) {
                    error = { 'required': true }
                } else if (numeric_value != null && condition_numeric_value != null) {
                    error = (numeric_value != condition_numeric_value) ? null :  { 'incorrect': true };
                } else if (condition.value && new_value == condition.value) {
                    error = { 'incorrect': true }
                }
                break; 
            case this.OP_EQ:
                if (!condition.value && new_value) {  // check empty or null
                    error = { 'incorrect': true }
                } else if (numeric_value != null && condition_numeric_value != null) {
                    error = (numeric_value == condition_numeric_value) ? null :  { 'incorrect': true };
                } else if (condition.value && new_value != condition.value) { // check value
                    error = { 'incorrect': true }                    
                }
                break;
            case this.OP_GTE:
                if (numeric_value != null && condition_numeric_value != null) {
                    error = (numeric_value >= condition_numeric_value) ? null :  { 'incorrect': true };
                } else {
                    error = (new_value && condition.value && new_value >= condition.value) ? null :  { 'incorrect': true };
                }
                break;
            case this.OP_GT:
                if (numeric_value != null && condition_numeric_value != null) {
                    error = (numeric_value > condition_numeric_value) ? null :  { 'incorrect': true };
                } else {
                    error = (new_value && condition.value && new_value > condition.value) ? null :  { 'incorrect': true };
                }
                break;
            case this.OP_LTE:
                if (numeric_value != null && condition_numeric_value != null) {
                    error = (numeric_value <= condition_numeric_value) ? null :  { 'incorrect': true };
                } else {
                    error = (new_value && condition.value && new_value <= condition.value) ? null :  { 'incorrect': true };
                }
                break;
            case this.OP_LT:
                if (numeric_value != null && condition_numeric_value != null) {
                    error = (numeric_value < condition_numeric_value) ? null :  { 'incorrect': true };
                } else {
                    error = (new_value && condition.value && new_value < condition.value) ? null :  { 'incorrect': true };
                }
                break;
        }
        
        return error;
    }
    //#endregion

    isNumeric(n) {
        return !isNaN(parseFloat(n)) && isFinite(n);
    }
}




// WEBPACK FOOTER //
// C:/Azure/Source/Workspaces/SalesNOW/SalesSOLO/WebSites/SalesSOLO.Web.Angular.telesales/src/app/services/condition.service.ts